import React from 'react';
import { IconButton, List, ListItem, ListItemText, Typography, ToggleButtonGroup, ToggleButton, ListItemAvatar, Avatar } from '@mui/material';
import { MessagingToDoStatus } from '@uplift/api';
import { useMessagingToDosWithStatusQuery, useMessagingToDoQuery, useCompleteMessagingToDoMutation, useArchiveMessagingToDoMutation, useUserDisplayNameQuery } from '@uplift/queries';
import { ClosableMenu, ClosableMenuItem } from '../../lib/simple-menu';
import { MoreVert } from '@mui/icons-material';
import { asyncWait, ellipsis } from '@uplift/utils';
import { withStyles } from 'tss-react/mui';
import { useMessagingContext } from '../MessagingProvider.component';

const ToDoSidebarItem: React.FC<{
  todoId: string;
  selected: boolean;
  onSelect: (todoId: string) => void;
}> = ({ todoId, selected, onSelect }) => {
  const anchorElRef = React.useRef<HTMLButtonElement | null>(null);
  const [ menuOpen, setMenuOpen ] = React.useState<boolean>(false);
  const { data: todo } = useMessagingToDoQuery(todoId, { suspense: true });
  const { data: participant } = useUserDisplayNameQuery(todo?.participantId || '', { suspense: true, enabled: !!todo?.participantId });
  const archiveMessagingToDoMutation = useArchiveMessagingToDoMutation();
  const completeMessagingToDoMutation = useCompleteMessagingToDoMutation();

  if (!participant) return null;
  if (!todo) return null;

  return (
    <ListItem
      alignItems="center"
      data-testid="todo-item"
      onClick={() => onSelect(todo.id)}
      secondaryAction={(
        <>
          <ClosableMenu
            anchorEl={anchorElRef.current}
            onClose={() => setMenuOpen(false)}
            open={menuOpen}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center'
            }}
          >
            <ClosableMenuItem
              disabled={!!todo.archivedAt}
              onClick={async () => {
                await Promise.all([
                  completeMessagingToDoMutation.mutateAsync({ todoId: todo.id, complete: !todo.completedAt }),
                  asyncWait(600),
                ]);
              }}
            >
              {todo.completedAt ? 'Uncomplete' : 'Complete'}
            </ClosableMenuItem>

            <ClosableMenuItem
              disabled={!!todo.completedAt}
              onClick={async () => {
                await Promise.all([
                  archiveMessagingToDoMutation.mutateAsync({ todoId: todo.id, archive: !todo.archivedAt }),
                  asyncWait(600),
                ]);
              }}
            >
              {todo.archivedAt ? 'Unarchive' : 'Archive'}
            </ClosableMenuItem>
          </ClosableMenu>
          <IconButton
            className="more-actions"
            ref={anchorElRef}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setMenuOpen(true);
            }}
          >
            <MoreVert />
          </IconButton>
        </>
      )}
      sx={{
        userSelect: 'none',
        '& .more-actions': {
          opacity: {
            md: menuOpen ? 1 : 0
          },
        },
        '&:hover .more-actions': {
          md: {
            opacity: 1,
          }
        },
        ...(selected ? {
          backgroundColor: 'grey.100',
          '&:hover': {
            backgroundColor: 'grey.100',
          }
        } : {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'grey.50',
          },
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar>{participant.first[0]}</Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ maxHeight: '3em', overflow: 'hidden' }}
        primary={ellipsis(participant.defaultDisplay, 36)}
        secondary={ellipsis(todo.description, 30)}
      />
    </ListItem>
  );
};


const ToDosStatusToggleButton = withStyles(ToggleButton, {
  root: {
    border: 'none',
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: 450,
    '&.Mui-selected': {
      fontWeight: 500,
      backgroundColor: 'transparent',
    },
  }
});


export const MessagingToDosFilterMenu: React.FC<{ value: MessagingToDoStatus; onChange: (value: MessagingToDoStatus) => void }> = ({ value, onChange }) => {
  const messagingContext = useMessagingContext();
  const { data: todo } = useMessagingToDoQuery(messagingContext.selectedToDoId || '', { enabled: !!messagingContext.selectedToDoId });

  React.useEffect(() => {
    if (!todo) return;
    if (!messagingContext.autoSelectToDoFilter) return;

    messagingContext.setAutoSelectToDoFilter(false);
    onChange(todo.status);
  }, [ todo, messagingContext.autoSelectToDoFilter ]);

  return (
    <ToggleButtonGroup
      size="small"
      fullWidth
      value={value}
      exclusive
      onChange={(_, v) => {
        v && onChange(v as MessagingToDoStatus);
      }}
    >
      <ToDosStatusToggleButton value="active">To Do</ToDosStatusToggleButton>
      <ToDosStatusToggleButton value="completed">Completed</ToDosStatusToggleButton>
      <ToDosStatusToggleButton value="archived">Archived</ToDosStatusToggleButton>
    </ToggleButtonGroup>
  );
};


export const MessagingToDosList: React.FC<{
  status: MessagingToDoStatus;
  selectedToDoId?: string;
  setSelectedToDoId: (selectedToDoId: string) => void;
}> = ({
  status,
  selectedToDoId,
  setSelectedToDoId
}) => {
  const { data: todos = [] } = useMessagingToDosWithStatusQuery(status ?? 'active', { suspense: true });

  if (todos.length === 0) {
    return (
      <Typography sx={{ pt: 3 }} align="center" color="textSecondary" height="100%" width="100%">No to dos found.</Typography>
    );
  }

  return (
    <List sx={{ width: '100%', minHeight: '100%', boxSizing: 'border-box' }}>
      {todos.map(todoId => (
        <ToDoSidebarItem
          key={todoId}
          todoId={todoId}
          selected={selectedToDoId === todoId}
          onSelect={setSelectedToDoId}
        />
      ))}
    </List>
  );
};
